<template>
  <div class="assessment-page">
    <div class="container" :style="{ minHeight: minHeight + 'px' }">
      <Head />
      <section class="assessment-hero">
        <img class="banner" src="./../../assets/image/back/qiyeziping_back.jpg" alt="企业评估">
        <div class="hero-shade"></div>
        <div class="hero-copy">
          <span class="hero-kicker">AI ENTERPRISE ASSESSMENT</span>
          <h1>人工智能企业创新能级评估</h1>
          <p>以数据客观呈现企业创新能力，辅助识别优势与提升方向</p>
        </div>
      </section>
      <main class="content">
        <div v-if="!showResult" class="assessment-form">
          <div class="form-intro">
            <div class="intro-copy">
              <span class="section-kicker">ASSESSMENT OVERVIEW</span>
              <h2>{{ isEditing ? '修改企业创新能力评估' : '开始企业创新能力评估' }}</h2>
              <p>请优先填写标有“核心必选指标”的 8 项内容，其他指标可根据企业实际情况选填。提交后系统将直接给出预备级、达标级和引领级自评测算结果。</p>
            </div>
            <div class="progress-panel">
              <div class="progress-number"><strong>{{ progressPercentage }}</strong><span>%</span></div>
              <div class="progress-detail">
                <div class="progress-title"><span>整体填写进度</span><b>{{ completedFieldCount }} / {{ totalFieldCount }} 个字段</b></div>
                <el-progress :percentage="progressPercentage" :show-text="false" :stroke-width="9" />
                <div class="progress-hint"><i class="el-icon-info"></i> 核心必选字段完成 {{ coreCompletedFieldCount }}/{{ coreRequiredFieldCount }} 后即可提交</div>
              </div>
            </div>
          </div>

          <nav class="category-tabs">
            <button v-for="category in categories" :key="category" type="button"
                    :class="['tab-item', { active: currentCategory === category }]"
                    @click="currentCategory = category">
              <span class="tab-index">{{ categories.indexOf(category) + 1 }}</span>
              <span class="tab-copy"><b>{{ category }}</b><small>{{ categoryCompleted(category) }}/{{ categoryTotal(category) }} 项已完成</small></span>
              <i v-if="categoryCompleted(category) === categoryTotal(category)" class="el-icon-success tab-done"></i>
            </button>
          </nav>

          <section class="indicators-container">
            <div v-for="group in currentGroups" :key="group.name" class="indicator-group">
              <div class="group-title">
                <span class="group-icon"><i class="el-icon-cpu"></i></span>
                <div><span>当前评估模块</span><h2>{{ group.name }}</h2></div>
                <b>{{ group.indicators.length }} 项指标</b>
              </div>
              <article v-for="indicator in group.indicators" :id="'indicator-' + indicator.id" :key="indicator.id"
                       :class="['indicator-item', { completed: isIndicatorComplete(indicator) }]">
                <header class="indicator-header">
                  <span class="indicator-id">{{ String(indicator.id).padStart(2, '0') }}</span>
                  <div class="indicator-heading">
                    <div class="indicator-name-row">
                      <h3 class="indicator-name">{{ indicator.name }}</h3>
                      <el-tag v-if="isCoreIndicator(indicator)" type="danger" size="mini">核心必选指标</el-tag>
                      <el-tag v-if="isStandardRequired(indicator)" class="standard-required-tag" size="mini">达标级必选</el-tag>
                      <el-tag v-if="isLeadingRequired(indicator)" class="leading-required-tag" size="mini">引领级必选</el-tag>
                      <el-tag v-if="indicator.isSpecial" type="warning" size="mini">特色指标</el-tag>
                      <el-tag v-for="tag in noRequirementTags(indicator)" :key="tag.key" :type="tag.type" size="mini">{{ tag.label }}</el-tag>
                      <el-tag v-if="indicator.evaluationMode !== 'automatic'" type="info" size="mini">需后续核验</el-tag>
                    </div>
                    <p class="indicator-description">{{ indicator.description }}</p>
                  </div>
                  <span class="complete-state"><i :class="isIndicatorComplete(indicator) ? 'el-icon-check' : 'el-icon-edit-outline'"></i>{{ isIndicatorComplete(indicator) ? '已完成' : '待填写' }}</span>
                </header>

                <div class="indicator-fields">
                  <div v-for="field in indicator.fields" :key="field.key"
                       :class="['field-item', { wide: field.type === 'textarea' || field.type === 'checkbox' }]">
                    <label class="field-label">
                      <span v-if="isCoreIndicator(indicator)" class="required-dot"></span>{{ field.label }}
                      <el-tooltip v-if="field.help" :content="field.help" placement="top" effect="light">
                        <i class="el-icon-question field-help"></i>
                      </el-tooltip>
                      <em v-if="field.unit">单位：{{ field.unit }}</em>
                    </label>
                    <el-input v-if="field.type === 'number'" v-model="formData[indicator.id][field.key]"
                              type="number" :min="field.allowNegative ? undefined : (field.min !== undefined ? field.min : 0)"
                              :max="field.max" class="number-input" placeholder="请输入数值"
                              @input="recalculateIndicator(indicator)">
                      <template v-if="field.unit" slot="append">{{ field.unit }}</template>
                    </el-input>
                    <el-input v-else-if="field.type === 'calculated'" :value="formData[indicator.id][field.key]"
                              disabled class="number-input calculated-input" placeholder="填写相关数据后自动计算">
                      <template v-if="field.unit" slot="append">{{ field.unit }}</template>
                    </el-input>
                    <el-input v-else-if="field.type === 'textarea'" v-model="formData[indicator.id][field.key]"
                              type="textarea" :rows="4" maxlength="2000" show-word-limit placeholder="请简要说明企业实际情况、成果或相关依据" />
                    <el-radio-group v-else-if="field.type === 'radio'" v-model="formData[indicator.id][field.key]">
                      <el-radio v-for="option in field.options" :key="option" :label="option">{{ option }}</el-radio>
                    </el-radio-group>
                    <el-select v-else-if="field.type === 'select'" v-model="formData[indicator.id][field.key]" :placeholder="field.placeholder || '请选择'">
                      <el-option v-for="option in field.options" :key="option.id" :label="option.label" :value="option.id" />
                    </el-select>
                    <el-checkbox-group v-else-if="field.type === 'checkbox'" v-model="formData[indicator.id][field.key]"
                                       @change="values => handleCheckboxChange(indicator.id, field, values)">
                      <el-checkbox v-for="option in field.options" :key="option.id" :label="option.id">{{ option.label }}</el-checkbox>
                    </el-checkbox-group>
                  </div>
                </div>

                <div v-if="needsEvidence(indicator)" class="evidence-section">
                  <div class="evidence-icon"><i class="el-icon-folder-opened"></i></div>
                  <div class="evidence-copy"><b>证明材料</b><span>提交评估后自动关联本项指标；支持 PDF、Word、Excel、JPG、PNG，单个不超过 20MB，最多 5 个。</span></div>
                  <el-upload action="#" :auto-upload="false" :on-change="file => handleFileChange(indicator.id, file)"
                             :on-remove="file => handleFileRemove(indicator.id, file)"
                             :before-remove="file => beforeEvidenceRemove(file)"
                             :file-list="evidenceFiles[indicator.id] || []" :limit="5"
                             accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png">
                    <el-button size="small" plain icon="el-icon-upload2">选择材料</el-button>
                  </el-upload>
                </div>
              </article>
            </div>
          </section>

          <div class="submit-bar">
            <div class="submit-status"><i class="el-icon-document-checked"></i><span><b>提交前请确认信息真实完整</b><small>核心必选字段 {{ coreCompletedFieldCount }}/{{ coreRequiredFieldCount }}；全部字段 {{ completedFieldCount }}/{{ totalFieldCount }}；{{ draftStatusText() }}</small></span></div>
            <div class="submit-actions">
              <el-button v-if="isEditing" plain @click="cancelEdit">返回评估结果</el-button>
              <el-button plain :loading="draftSaving" @click="saveCurrentDraft(true)">暂存草稿</el-button>
              <el-button type="primary" size="large" :loading="submitting" @click="handleSubmit">{{ isEditing ? '保存修改并重新提交' : '确认并提交评估' }} <i class="el-icon-right"></i></el-button>
            </div>
          </div>
        </div>

        <section v-else class="result-container">
          <h1>{{ reviewCompleted ? '人工智能企业创新能级正式核验结果' : '人工智能企业创新能级评估已提交' }}</h1>
          <div class="highest-level">
            <span>{{ reviewCompleted ? '正式核验等级' : '自评测算等级' }}</span>
            <strong>{{ reviewCompleted ? levelName(evaluationResult.record.verifiedLevel) : levelName(evaluationResult.selfAssessmentLevel) }}</strong>
            <el-tag :type="reviewCompleted ? 'success' : 'warning'">
              {{ reviewCompleted ? '核验完成' : '仅供参考' }}
            </el-tag>
          </div>
          <h2 v-if="!reviewCompleted" class="reference-title">各等级自评测算明细</h2>
          <div v-if="!reviewCompleted" class="level-list">
            <div v-for="item in levelResults" :key="item.key" :class="['level-row', { 'preparatory-row': item.key === 'preparatory' }]">
              <span>{{ item.name }}</span>
              <span :class="item.result.passed ? 'status-pass' : 'status-fail'">
                <i :class="item.result.passed ? 'el-icon-success' : 'el-icon-error'" />
                {{ item.result.passed ? '已达到' : '未达到' }}
              </span>
              <template v-if="item.key === 'preparatory'">
                <span class="preparatory-summary">指定指标 {{ requiredPassed(item.result) }}/{{ item.result.totalRequired || 7 }}</span>
                <span v-if="item.result.missingIndicatorIds && item.result.missingIndicatorIds.length" class="missing-indicators">
                  未通过：{{ missingIndicatorText(item.result) }}
                </span>
                <span v-else class="all-required-passed">7 项指定指标均已通过</span>
              </template>
              <template v-else>
                <span>累计通过 {{ item.result.totalPassed }} 项（门槛 {{ levelTarget(item.key, 'total') }}）</span>
                <span>通用通过 {{ item.result.generalPassed }} 项（门槛 {{ levelTarget(item.key, 'general') }}）</span>
                <span v-if="item.result.coreGeneralRequired > 0">指定通用 {{ item.result.coreGeneralPassed }}/{{ item.result.coreGeneralRequired }}</span>
                <span>核心特色 {{ item.result.coreSpecialPassed }}/{{ levelTarget(item.key, 'core') }}</span>
                <span>专长特色 {{ item.result.optionalSpecialPassed }}/2</span>
              </template>
            </div>
          </div>
          <p v-if="reviewCompleted" class="review-note">该等级由审核人员依据企业填报内容和证明材料核验形成。</p>
          <div class="result-support">
            <div class="support-contacts">
              <b>联盟老师联系方式</b>
              <span>郭老师：15201226193</span>
              <span>段老师：13811203300</span>
              <span>李老师：18500220825</span>
            </div>
            <p class="disclaimer">本结果仅供企业自评参考，不具备任何法律效力。企业技术中心创新能力评估结果，以北京市经济和信息化局“<a href="http://jxj.beijing.gov.cn/bsfw/bsrk" target="_blank" rel="noopener noreferrer">企业技术中心梯度培育平台</a>”评估结果为准。</p>
          </div>
          <div class="result-actions">
            <el-button type="primary" icon="el-icon-edit" @click="editAssessment">查看并修改填报内容</el-button>
          </div>
        </section>
      </main>
    </div>
    <Footer />
  </div>
</template>

<script>
import Head from "@/components/Head.vue";
import Footer from "@/components/Footer.vue";
import indicators, { CORE_REQUIRED_INDICATOR_IDS } from "@/assessment/indicators";
import { submitAiAssessment, getLatestAiAssessment, uploadAiEvidence } from "@/api/selfassessment";
import assessmentDraftMixin from "@/assessment/assessmentDraftMixin";

const STANDARD_REQUIRED_INDICATOR_IDS = [24, 25];
const LEADING_REQUIRED_INDICATOR_IDS = [29];

export default {
  name: "AIAssessment",
  components: { Head, Footer },
  mixins: [assessmentDraftMixin],
  assessmentDraftDomain: "ai",
  data() {
    return {
      minHeight: 0,
      currentCategory: "创新环境",
      formData: {},
      evidenceFiles: {},
      checkboxPrevious: {},
      showResult: false,
      isEditing: false,
      submitting: false,
      evaluationResult: null
    };
  },
  computed: {
    categories() { return [...new Set(indicators.map(item => item.level1))]; },
    currentGroups() {
      const groups = [];
      indicators.filter(item => item.level1 === this.currentCategory).forEach(indicator => {
        let group = groups.find(item => item.name === indicator.level2);
        if (!group) { group = { name: indicator.level2, indicators: [] }; groups.push(group); }
        group.indicators.push(indicator);
      });
      return groups;
    },
    totalFieldCount() { return indicators.reduce((total, item) => total + item.fields.length, 0); },
    completedFieldCount() {
      let count = 0;
      indicators.forEach(indicator => indicator.fields.forEach(field => {
        if (!this.isEmpty(this.formData[indicator.id][field.key])) count++;
      }));
      return count;
    },
    coreRequiredFieldCount() {
      return indicators.filter(item => CORE_REQUIRED_INDICATOR_IDS.includes(item.id))
        .reduce((total, item) => total + item.fields.filter(field => field.type !== "calculated").length, 0);
    },
    coreCompletedFieldCount() {
      return indicators.filter(item => CORE_REQUIRED_INDICATOR_IDS.includes(item.id)).reduce((total, indicator) => {
        return total + indicator.fields.filter(field => field.type !== "calculated" && !this.isEmpty(this.formData[indicator.id][field.key])).length;
      }, 0);
    },
    progressPercentage() { return Math.round(this.completedFieldCount * 100 / this.totalFieldCount); },
    reviewCompleted() {
      return Boolean(this.evaluationResult && this.evaluationResult.record && this.evaluationResult.record.reviewStatus === "completed");
    },
    levelResults() {
      if (!this.evaluationResult) return [];
      return [
        { key: "preparatory", name: "预备级", result: this.evaluationResult.preparatory },
        { key: "standard", name: "达标级", result: this.evaluationResult.standard },
        { key: "leading", name: "引领级", result: this.evaluationResult.leading }
      ];
    }
  },
  created() {
    this.initFormData();
    this.loadLatest();
  },
  methods: {
    initFormData() {
      indicators.forEach(indicator => {
        this.$set(this.formData, indicator.id, {});
        indicator.fields.forEach(field => this.$set(this.formData[indicator.id], field.key, field.type === "checkbox" ? [] : ""));
      });
    },
    async loadLatest() {
      let result = null;
      try {
        result = await getLatestAiAssessment();
      } catch (error) { /* 未登录或暂无记录时保持填写页 */ }
      await this.initializeAssessmentDraft(result);
    },
    hydrateForm(result) {
      this.formData = {};
      this.evidenceFiles = {};
      this.checkboxPrevious = {};
      this.initFormData();
      (result.answers || []).forEach(answer => {
        let values = answer.answerJson;
        if (typeof values === "string") {
          try { values = JSON.parse(values); } catch (error) { values = {}; }
        }
        if (!values || typeof values !== "object") return;
        const indicator = indicators.find(item => Number(item.id) === Number(answer.indicatorId));
        if (!indicator) return;
        indicator.fields.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(values, field.key)) this.$set(this.formData[indicator.id], field.key, values[field.key]);
          if (field.type === "checkbox") this.$set(this.checkboxPrevious, `${indicator.id}:${field.key}`, (values[field.key] || []).slice());
        });
      });
      (result.evidence || []).forEach(item => {
        const files = this.evidenceFiles[item.indicatorId] || [];
        this.$set(this.evidenceFiles, item.indicatorId, files.concat({
          uid: `existing-${item.id}`,
          name: item.fileName,
          url: item.fileUri,
          status: "success",
          existing: true
        }));
      });
      this.syncLinkedCalculatedFields();
    },
    needsEvidence(indicator) { return indicator.evaluationMode === "manual" || indicator.evaluationMode === "hybrid"; },
    isCoreIndicator(indicator) { return CORE_REQUIRED_INDICATOR_IDS.includes(indicator.id); },
    isStandardRequired(indicator) { return STANDARD_REQUIRED_INDICATOR_IDS.includes(indicator.id); },
    isLeadingRequired(indicator) { return LEADING_REQUIRED_INDICATOR_IDS.includes(indicator.id); },
    noRequirementTags(indicator) {
      return [
        { key: "preparatory", label: "预备级无强制要求", type: "success" },
        { key: "standard", label: "达标级无强制要求", type: "warning" },
        { key: "leading", label: "引领级无强制要求", type: "info" }
      ].filter(item => !indicator.requirements[item.key]);
    },
    isIndicatorComplete(indicator) {
      return indicator.fields.every(field => !this.isEmpty(this.formData[indicator.id][field.key]));
    },
    categoryTotal(category) { return indicators.filter(item => item.level1 === category).length; },
    categoryCompleted(category) {
      return indicators.filter(item => item.level1 === category && this.isIndicatorComplete(item)).length;
    },
    handleFileChange(indicatorId, file) {
      const files = this.evidenceFiles[indicatorId] || [];
      if (!files.some(item => item.uid === file.uid)) this.$set(this.evidenceFiles, indicatorId, files.concat(file));
    },
    handleFileRemove(indicatorId, file) {
      this.$set(this.evidenceFiles, indicatorId, (this.evidenceFiles[indicatorId] || []).filter(item => item.uid !== file.uid));
    },
    beforeEvidenceRemove(file) {
      if (!file.existing) return true;
      this.$message.info("已提交的证明材料将继续保留；如需替换，可补充上传新材料");
      return false;
    },
    handleCheckboxChange(indicatorId, field, values) {
      if (!field.options || !field.options.some(option => option.id === "none")) return;
      const stateKey = `${indicatorId}:${field.key}`;
      const previous = this.checkboxPrevious[stateKey] || [];
      let next = values.slice();
      if (next.includes("none") && !previous.includes("none")) next = ["none"];
      else if (next.includes("none") && next.length > 1) next = next.filter(value => value !== "none");
      this.$set(this.formData[indicatorId], field.key, next);
      this.$set(this.checkboxPrevious, stateKey, next.slice());
    },
    recalculateIndicator(indicator) {
      this.$nextTick(() => {
        indicator.fields.filter(field => field.type === "calculated" && field.formula !== "linked").forEach(field => {
          const sourceValues = field.fields.map(key => this.formData[indicator.id][key]);
          this.$set(this.formData[indicator.id], field.key, this.calculateValue(field.formula, sourceValues));
        });
        this.syncLinkedCalculatedFields();
      });
    },
    syncLinkedCalculatedFields() {
      indicators.forEach(indicator => indicator.fields.filter(field => field.formula === "linked").forEach(field => {
        const source = this.formData[field.sourceIndicator];
        this.$set(this.formData[indicator.id], field.key, source ? source[field.sourceField] : "");
      }));
    },
    calculateValue(formula, values) {
      if (values.some(value => this.isEmpty(value))) return "";
      const numbers = values.map(Number);
      if (numbers.some(value => !Number.isFinite(value))) return "";
      let result;
      if (formula === "reductionRate") {
        if (numbers[1] <= 0) return "";
        result = (numbers[1] - numbers[0]) / numbers[1] * 100;
      } else if (formula === "growthRate") {
        if (numbers[0] <= 0) return "";
        result = (numbers[1] - numbers[0]) / numbers[0] * 100;
      } else if (formula === "cagr") {
        if (numbers[0] <= 0 || numbers[1] < 0) return "";
        result = (Math.pow(numbers[1] / numbers[0], 1 / 3) - 1) * 100;
      } else return "";
      return Number(result.toFixed(2));
    },
    async handleSubmit() {
      const missing = this.firstMissingField();
      if (missing) {
        this.currentCategory = missing.indicator.level1;
        this.$message.warning(`请填写第${missing.indicator.id}项“${missing.field.label}”`);
        this.$nextTick(() => {
          const target = document.getElementById(`indicator-${missing.indicator.id}`);
          if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        return;
      }
      this.submitting = true;
      const wasEditing = this.isEditing;
      try {
        const result = await submitAiAssessment(this.formData);
        const failedUploads = [];
        for (const id of Object.keys(this.evidenceFiles)) {
          const files = (this.evidenceFiles[id] || []).filter(file => file.raw && !file.existing);
          if (!files || files.length === 0) continue;
          try { await uploadAiEvidence(result.assessmentId, id, files); }
          catch (error) { failedUploads.push(id); }
        }
        await this.clearCurrentDraft();
        this.evaluationResult = await getLatestAiAssessment();
        this.showResult = true;
        this.isEditing = false;
        window.scrollTo(0, 300);
        if (failedUploads.length) this.$message.warning(`评估已保存，第${failedUploads.join("、")}项材料上传失败，请稍后补充`);
        else this.$message.success(wasEditing ? "修改已保存，评估已重新提交" : "评估及证明材料已保存");
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || "提交失败，请检查填写内容后重试");
      } finally { this.submitting = false; }
    },
    firstMissingField() {
      for (const indicator of indicators.filter(item => this.isCoreIndicator(item))) for (const field of indicator.fields) {
        if (field.type === "calculated") continue;
        if (this.isEmpty(this.formData[indicator.id][field.key])) return { indicator, field };
      }
      return null;
    },
    isEmpty(value) { return value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0); },
    editAssessment() {
      if (!this.evaluationResult) return;
      this.hydrateForm(this.evaluationResult);
      this.isEditing = true;
      this.showResult = false;
      this.currentCategory = this.categories[0];
      window.scrollTo(0, 300);
    },
    async cancelEdit() {
      await this.clearCurrentDraft();
      this.isEditing = false;
      this.showResult = true;
      window.scrollTo(0, 300);
    },
    levelName(level) { return { NONE: "未定级", PREPARATORY: "预备级", STANDARD: "达标级", LEADING: "引领级" }[level] || "未定级"; },
    levelTarget(level, kind) {
      const targets = { preparatory: { total: 7, general: 4, core: 3 }, standard: { total: 15, general: 9, core: 4 }, leading: { total: 20, general: 13, core: 5 } };
      return targets[level][kind];
    },
    requiredPassed(result) {
      const required = result.totalRequired || 7;
      return Math.max(0, required - ((result.missingIndicatorIds || []).length));
    },
    missingIndicatorText(result) {
      return (result.missingIndicatorIds || []).map(id => {
        const indicator = indicators.find(item => Number(item.id) === Number(id));
        return indicator ? `第${id}项 ${indicator.name}` : `第${id}项`;
      }).join('、');
    },
    resizePage() { this.minHeight = document.documentElement.clientHeight - 200; }
  },
  mounted() {
    this.minHeight = document.documentElement.clientHeight - 200;
    window.addEventListener("resize", this.resizePage);
  },
  beforeDestroy() { window.removeEventListener("resize", this.resizePage); }
};
</script>

<style lang="scss" scoped>
.assessment-page { background: #f3f7fb; color: #1d2b3a; }
.assessment-hero { position: relative; height: 285px; overflow: hidden; }
.banner { width: 100%; height: 100%; object-fit: cover; }
.hero-shade { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(5,31,73,.88) 0%, rgba(0,99,153,.66) 48%, rgba(8,186,194,.22) 100%); }
.hero-copy { position: absolute; left: 50%; bottom: 64px; width: 1180px; max-width: calc(100% - 48px); transform: translateX(-50%); color: #fff; text-shadow: 0 2px 12px rgba(0,24,56,.3); }
.hero-kicker,.section-kicker { display: inline-block; color: #40d6e4; font-size: 12px; font-weight: 700; letter-spacing: 2px; }
.hero-copy h1 { margin: 8px 0 10px; font-size: 34px; line-height: 1.25; letter-spacing: 1px; }
.hero-copy p { margin: 0; color: rgba(255,255,255,.82); font-size: 15px; }
.content { position: relative; width: 1180px; max-width: calc(100% - 40px); margin: -34px auto 56px; color: #263442; z-index: 2; }
.assessment-form { padding-bottom: 6px; }
.form-intro { display: grid; grid-template-columns: minmax(0,1.35fr) minmax(360px,.9fr); gap: 46px; align-items: center; padding: 28px 32px; background: #fff; border: 1px solid rgba(220,229,238,.8); border-radius: 12px; box-shadow: 0 12px 36px rgba(31,64,96,.1); }
.section-kicker { color: #1685c0; letter-spacing: 1.5px; }
.intro-copy h2 { margin: 7px 0 9px; color: #17324d; font-size: 22px; }
.intro-copy p { margin: 0; color: #718091; font-size: 14px; line-height: 1.8; }
.progress-panel { display: flex; align-items: center; gap: 18px; padding: 18px 20px; background: linear-gradient(135deg,#f2f8fd,#f8fbfe); border: 1px solid #e5eef6; border-radius: 10px; }
.progress-number { flex: 0 0 62px; color: #0b82c3; line-height: 1; text-align: center; }
.progress-number strong { font-size: 34px; font-weight: 700; }
.progress-number span { font-size: 14px; font-weight: 600; }
.progress-detail { flex: 1; min-width: 0; }
.progress-title { display: flex; justify-content: space-between; margin-bottom: 9px; color: #4f6071; font-size: 13px; }
.progress-title b { color: #17324d; font-weight: 600; }
.progress-hint { margin-top: 9px; color: #8995a3; font-size: 12px; }
.progress-hint i { margin-right: 4px; color: #5aa7d2; }
.category-tabs { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; margin: 22px 0 14px; padding: 9px; background: #eaf1f7; border-radius: 12px; }
.tab-item { position: relative; display: flex; align-items: center; gap: 11px; min-width: 0; padding: 13px 14px; border: 1px solid transparent; border-radius: 8px; background: transparent; color: #657587; cursor: pointer; text-align: left; transition: all .2s ease; }
.tab-item:hover { color: #087ebd; background: rgba(255,255,255,.7); }
.tab-item.active { color: #0a75ae; background: #fff; border-color: #dce9f2; box-shadow: 0 4px 12px rgba(34,76,111,.09); }
.tab-index { display: inline-flex; flex: 0 0 28px; height: 28px; align-items: center; justify-content: center; border-radius: 8px; background: #d8e6f0; color: #527087; font-size: 13px; font-weight: 700; }
.tab-item.active .tab-index { color: #fff; background: linear-gradient(135deg,#0d8ed0,#18b0bf); }
.tab-copy { display: flex; min-width: 0; flex-direction: column; gap: 4px; }
.tab-copy b { font-size: 15px; white-space: nowrap; }
.tab-copy small { color: #9aa6b1; font-size: 11px; white-space: nowrap; }
.tab-done { margin-left: auto; color: #23a66f; font-size: 16px; }
.indicators-container { overflow: hidden; background: #fff; border: 1px solid #e2eaf1; border-radius: 12px; box-shadow: 0 10px 30px rgba(34,68,99,.06); }
.indicator-group { padding: 0 28px 10px; }
.group-title { display: flex; align-items: center; gap: 13px; margin: 0 -28px 2px; padding: 19px 28px; background: linear-gradient(90deg,#f0f7fc,#f8fbfd); border-bottom: 1px solid #e3edf4; }
.group-icon { display: inline-flex; width: 38px; height: 38px; align-items: center; justify-content: center; border-radius: 10px; color: #fff; background: linear-gradient(135deg,#0785c8,#16aabf); font-size: 18px; box-shadow: 0 5px 12px rgba(9,139,190,.2); }
.group-title div > span { color: #8b9aa8; font-size: 11px; letter-spacing: 1px; }
.group-title h2 { margin: 3px 0 0; color: #194d70; font-size: 18px; }
.group-title > b { margin-left: auto; padding: 5px 11px; border-radius: 14px; color: #568099; background: #e4f0f6; font-size: 12px; font-weight: 500; }
.indicator-item { position: relative; margin: 20px 0; padding: 24px 24px 22px; border: 1px solid #e7edf3; border-radius: 10px; background: #fff; transition: border-color .2s,box-shadow .2s; scroll-margin-top: 120px; }
.indicator-item:hover { border-color: #cbddea; box-shadow: 0 8px 22px rgba(40,75,105,.07); }
.indicator-item.completed { border-color: #cce8de; }
.indicator-item.completed:before { position: absolute; top: 0; bottom: 0; left: 0; width: 3px; border-radius: 10px 0 0 10px; background: #2eb27b; content: ""; }
.indicator-header { display: flex; align-items: flex-start; gap: 14px; }
.indicator-id { display: inline-flex; flex: 0 0 42px; height: 42px; align-items: center; justify-content: center; border-radius: 11px; color: #087fbd; background: #eaf6fc; font-size: 14px; font-weight: 700; }
.indicator-heading { flex: 1; min-width: 0; text-align: left; }
.indicator-name-row { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; min-height: 24px; }
.standard-required-tag { color: #1677b8; background: #eaf6fd; border-color: #9fcfea; }
.leading-required-tag { color: #7350b5; background: #f3effc; border-color: #c9b8eb; }
.indicator-name { margin: 0; color: #1c3145; font-size: 17px; font-weight: 650; }
.indicator-description { margin: 7px 0 0; color: #84909d; font-size: 13px; line-height: 1.65; text-align: left; }
.complete-state { flex: 0 0 auto; padding: 5px 9px; border-radius: 14px; color: #9aa4ae; background: #f3f5f7; font-size: 11px; }
.complete-state i { margin-right: 4px; }
.completed .complete-state { color: #23875f; background: #e9f7f1; }
.indicator-fields { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 20px 26px; margin: 23px 0 0 56px; padding-top: 21px; border-top: 1px dashed #e2e9ef; }
.field-item { min-width: 0; text-align: left; }
.field-item.wide { grid-column: 1 / -1; }
.field-label { display: flex; align-items: center; margin-bottom: 10px; color: #465769; font-size: 14px; font-weight: 600; }
.field-label em { margin-left: auto; color: #9aa6b2; font-size: 12px; font-style: normal; font-weight: 400; }
.required-dot { width: 4px; height: 4px; margin-right: 7px; border-radius: 50%; background: #ef765f; }
.field-help { margin-left: 6px; color: #1594c7; font-size: 15px; cursor: help; }
.number-input { width: 100%; max-width: 380px; margin: 0 !important; vertical-align: top; }
.calculated-input ::v-deep .el-input__inner { color: #1685c0; background: #f3f8fc; font-weight: 600; }
.evidence-section { display: flex; align-items: center; gap: 14px; margin: 22px 0 0 56px; padding: 15px 18px; background: #f6f9fc; border: 1px dashed #cbdce8; border-radius: 9px; }
.evidence-icon { display: inline-flex; flex: 0 0 38px; height: 38px; align-items: center; justify-content: center; border-radius: 9px; color: #218abf; background: #e5f2f9; font-size: 18px; }
.evidence-copy { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 4px; }
.evidence-copy b { color: #3f5568; font-size: 14px; }
.evidence-copy span { color: #8a98a5; font-size: 12px; line-height: 1.5; }
.submit-bar { position: sticky; bottom: 16px; display: flex; align-items: center; justify-content: space-between; margin: 16px 12px 0; padding: 14px 16px 14px 20px; background: rgba(255,255,255,.96); border: 1px solid #dbe6ee; border-radius: 11px; box-shadow: 0 10px 30px rgba(25,60,91,.16); backdrop-filter: blur(10px); z-index: 5; }
.submit-status { display: flex; align-items: center; gap: 11px; color: #4e6172; }
.submit-status > i { color: #168bc6; font-size: 25px; }
.submit-status span { display: flex; flex-direction: column; gap: 3px; }
.submit-status b { font-size: 13px; }
.submit-status small { color: #8c99a5; font-size: 11px; }
.submit-actions { display: flex; align-items: center; gap: 10px; }
.submit-bar .el-button { min-width: 190px; height: 44px; border: 0; border-radius: 8px; background: linear-gradient(90deg,#0788cc,#12a8ba); box-shadow: 0 6px 14px rgba(7,137,197,.22); font-weight: 600; }
.submit-bar .submit-actions .el-button.is-plain { min-width: 120px; color: #537083; background: #fff; border: 1px solid #d7e2ea; box-shadow: none; }
.submit-bar .el-button i { margin-left: 6px; }
.result-container { padding: 34px; background: #fff; border: 1px solid #e2eaf1; border-radius: 12px; box-shadow: 0 12px 36px rgba(31,64,96,.1); }
.result-container h1 { margin-top: 0; color: #17324d; text-align: center; font-size: 25px; }
.highest-level { display: flex; justify-content: center; align-items: center; gap: 20px; margin: 28px 0; padding: 26px; background: linear-gradient(135deg,#eef7fc,#f7fbfd); border-radius: 10px; }
.highest-level strong { color: #087ebd; font-size: 32px; }
.reference-title { margin: 0 0 12px; color: #506579; font-size: 15px; font-weight: 600; }
.level-list { overflow: hidden; border: 1px solid #e7edf2; border-radius: 9px; }
.level-row { display: grid; grid-template-columns: 90px 110px repeat(5,1fr); gap: 12px; align-items: center; padding: 17px 14px; border-bottom: 1px solid #ebeef5; font-size: 13px; }
.level-row:last-child { border-bottom: 0; }
.preparatory-summary { color: #17324d; font-weight: 600; }
.missing-indicators,.all-required-passed { grid-column: span 3; line-height: 1.6; }
.missing-indicators { color: #b56a3a; }
.all-required-passed { color: #2d9c6d; }
.status-pass { color: #2d9c6d; }.status-fail { color: #d85a55; }
.review-note { padding: 14px 18px; border-radius: 8px; color: #786840; background: #fff8e8; line-height: 1.7; }
.result-support { margin-top: 16px; padding: 18px 20px; border: 1px solid #dfe9f1; border-radius: 9px; background: #f7fafc; color: #536779; }
.support-contacts { display: flex; flex-wrap: wrap; gap: 10px 28px; align-items: center; }
.support-contacts b { color: #24435e; }
.support-contacts span { white-space: nowrap; }
.disclaimer { margin: 15px 0 0; padding-top: 14px; border-top: 1px dashed #d7e2ea; color: #758595; font-size: 13px; line-height: 1.8; }
.disclaimer a { color: #087ebd; text-decoration: none; }
.disclaimer a:hover { text-decoration: underline; }
.result-actions { margin-top: 24px; text-align: center; }
.assessment-page ::v-deep .el-progress-bar__outer { background: #dfe8ef; }
.assessment-page ::v-deep .el-progress-bar__inner { background: linear-gradient(90deg,#0789cd,#12b6b5); }
.assessment-page ::v-deep .el-input__inner,.assessment-page ::v-deep .el-textarea__inner { border-color: #dce5ed; border-radius: 7px; color: #35485a; transition: all .2s; }
.assessment-page ::v-deep .el-input__inner { height: 42px; }
.assessment-page ::v-deep .el-input__inner:focus,.assessment-page ::v-deep .el-textarea__inner:focus { border-color: #1594c7; box-shadow: 0 0 0 3px rgba(21,148,199,.09); }
.assessment-page ::v-deep .el-checkbox-group,.assessment-page ::v-deep .el-radio-group { display: flex; flex-wrap: wrap; gap: 10px; }
.assessment-page ::v-deep .el-checkbox,.assessment-page ::v-deep .el-radio { display: inline-flex; align-items: center; min-height: 38px; margin: 0; padding: 0 14px; border: 1px solid #dce5ed; border-radius: 7px; background: #fff; }
.assessment-page ::v-deep .el-checkbox.is-checked,.assessment-page ::v-deep .el-radio.is-checked { border-color: #73b8d7; background: #eef8fc; }
.assessment-page ::v-deep .el-checkbox__label,.assessment-page ::v-deep .el-radio__label { color: #536373; font-size: 13px; }
.assessment-page ::v-deep .el-select { width: 100%; max-width: 400px; }
.assessment-page ::v-deep .el-upload-list { margin-bottom: 6px; }

@media (max-width: 900px) {
  .form-intro { grid-template-columns: 1fr; gap: 20px; }
  .category-tabs { grid-template-columns: repeat(2,1fr); }
  .indicator-fields { grid-template-columns: 1fr; }
  .field-item.wide { grid-column: auto; }
  .level-row { grid-template-columns: repeat(2,1fr); }
  .missing-indicators,.all-required-passed { grid-column: 1 / -1; }
}
@media (max-width: 768px) {
  .assessment-hero { height: 220px; }.hero-copy { bottom: 34px; }.hero-copy h1 { font-size: 25px; }.hero-copy p { font-size: 13px; }
  .content { max-width: calc(100% - 20px); margin-top: -18px; }.form-intro { padding: 22px 18px; }.progress-panel { padding: 15px 12px; }
  .category-tabs { grid-template-columns: 1fr 1fr; gap: 6px; }.tab-item { padding: 11px 8px; }.tab-copy small { display: none; }
  .indicator-group { padding: 0 10px 6px; }.group-title { margin: 0 -10px; padding: 16px 14px; }
  .indicator-item { margin: 10px 0; padding: 18px 14px; }.indicator-id { flex-basis: 36px; height: 36px; }.complete-state { display: none; }
  .indicator-fields,.evidence-section { margin-left: 0; }.evidence-section { align-items: flex-start; flex-wrap: wrap; }.evidence-copy { min-width: calc(100% - 56px); }
  .submit-bar { bottom: 8px; margin: 10px 0 0; padding: 11px; }.submit-status { display: none; }.submit-actions { width: 100%; }.submit-bar .el-button { width: 100%; min-width: 0; }
  .support-contacts { align-items: flex-start; flex-direction: column; gap: 7px; }
  .highest-level { flex-direction: column; gap: 10px; }.level-row { grid-template-columns: 1fr 1fr; }.result-container { padding: 22px 14px; }
}
</style>
